/**
 * DataSortHelper
 *
 * Re-orders and re-shapes the answer data structures so the rendered table matches
 * the user's original left-panel selection order:
 *  - `sortOutputData` sorts OutputdataObject rows/columns (and their nested arrays)
 *    to follow the order of the corresponding `dataObject` selection,
 *  - `reverseTranspose` / `handleOutputdataObjectOnTransposeSelect` swap Row and
 *    Column when transpose is toggled,
 *  - `manipulateIndexData` rebases every share value against the selected index
 *    column, honouring the weighted/unweighted choice and nesting level.
 *
 * NOTE: these keep the original `const target = block || comp;` idiom - when no
 * block is passed the live component's own data is operated on.
 *
 * Extracted verbatim from chatbotexpand.component.ts (`this.` -> `comp.`);
 * behavior is unchanged.
 */
export class DataSortHelper {

  static onTableIconClick(comp: any, block: any = null) {
    const target = block || comp;
    target.isTableClick = true;
    comp.updateChartDisablingFlags(block);
    comp.resetChartStates('table', block);
  }

  static sortOutputData(comp: any, block: any = null) {
    const target = block || comp;
    if (!target.OutputdataObject) return;

    const targetDataObject = target.dataObject && target.dataObject.Row && target.dataObject.Row.length > 0 ? target.dataObject : comp.dataObject;

    target.OutputdataObject.Row.sort((a: any, b: any) => {
      const indexA = targetDataObject.Row.findIndex(
        (item: any) => item.Parent === a.Parent
      );
      const indexB = targetDataObject.Row.findIndex(
        (item: any) => item.Parent === b.Parent
      );
      return indexA - indexB;
    });

    target.OutputdataObject.Column.sort((a: any, b: any) => {
      const indexA = targetDataObject.Column.findIndex(
        (item: any) => item.Parent === a.Parent
      );
      const indexB = targetDataObject.Column.findIndex(
        (item: any) => item.Parent === b.Parent
      );
      return indexA - indexB;
    });

    target.OutputdataObject.Row.forEach((row: any) => {
      const parentData = targetDataObject.Row.find(
        (item: any) => item.Parent === row.Parent
      );
      if (parentData) {
        row.Data.sort((a: any, b: any) => {
          const indexA = parentData.Data.findIndex(
            (data: any) => data.Child === a.Child
          );
          const indexB = parentData.Data.findIndex(
            (data: any) => data.Child === b.Child
          );
          return indexA - indexB;
        });
      }
    });

    target.OutputdataObject.Row.forEach((row: any) => {
      row.Data.forEach((outputSelection: any) => {
        targetDataObject.RowNesting.forEach((rowNest: any) => {
          comp.sortByChildIndex(outputSelection.NestedArray, rowNest.Data);
        });
      });
    });

    target.OutputdataObject.Row.forEach((row: any) => {
      row.Data.forEach((outputSelection: any) => {
        outputSelection.NestedArray.forEach((nestedItem: any) => {
          targetDataObject.RowNesting.forEach((rowNest: any) => {
            comp.sortByChildIndex(nestedItem.NestedArray, rowNest.Data);
          });
        });
      });
    });

    target.OutputdataObject.Column.forEach((col: any) => {
      const parentData = targetDataObject.Column.find(
        (item: any) => item.Parent === col.Parent
      );
      if (parentData) {
        col.Data.sort((a: any, b: any) => {
          const indexA = parentData.Data.findIndex(
            (data: any) => data.Child === a.Child
          );
          const indexB = parentData.Data.findIndex(
            (data: any) => data.Child === b.Child
          );
          return indexA - indexB;
        });
      }
    });

    target.OutputdataObject.Column.forEach((col: any) => {
      col.Data.forEach((outputSelection: any) => {
        targetDataObject.ColNesting.forEach((colNest: any) => {
          comp.sortByChildIndex(outputSelection.NestedArray, colNest.Data);
        });
      });
    });

    target.OutputdataObject.Column.forEach((col: any) => {
      col.Data.forEach((outputSelection: any) => {
        outputSelection.NestedArray.forEach((nestedItem: any) => {
          targetDataObject.ColNesting.forEach((colNest: any) => {
            comp.sortByChildIndex(nestedItem.NestedArray, colNest.Data);
          });
        });
      });
    });
    target.OutputdataObject = JSON.parse(JSON.stringify(target.OutputdataObject));
  }

  static reverseTranspose(comp: any) {
    if (
      comp.OutputdataObject != null &&
      (comp.OutputdataObject['Row'].length > 0 ||
        comp.OutputdataObject['Column'].length > 0)
    ) {
      let tempRow = [...comp.OutputdataObject.Column];
      let tempColumn = [...comp.OutputdataObject.Row];
      comp.OutputdataObject.Row = [...tempRow];
      comp.OutputdataObject.Column = [...tempColumn];
    }
  }

  static handleOutputdataObjectOnTransposeSelect(comp: any, block: any = null) {
    const target = block || comp;
    if (
      target.OutputdataObject != null &&
      (target.OutputdataObject['Row'].length > 0 ||
        target.OutputdataObject['Column'].length > 0)
    ) {
      let tempRow = [...target.OutputdataObject.Row];
      let tempColumn = [...target.OutputdataObject.Column];
      target.OutputdataObject.Row = [...tempColumn];
      target.OutputdataObject.Column = [...tempRow];
    }
    if (
      target.builtTableData != null &&
      target.builtTableData !== target.OutputdataObject &&
      (target.builtTableData['Row'].length > 0 ||
        target.builtTableData['Column'].length > 0)
    ) {
      let tempRow = [...target.builtTableData.Row];
      let tempColumn = [...target.builtTableData.Column];
      target.builtTableData.Row = [...tempColumn];
      target.builtTableData.Column = [...tempRow];
    }
  }

  static manipulateIndexData(comp: any, block: any = null) {
    const target = block || comp;
    let level = 0;
    if (comp.dataObject.ColNesting.length > 0) {
      level = comp.dataObject.ColNesting.length;
    }
    const dataToUse = target.CrossTabOutPutData || [];
    const selectedIndex = target.selectedIndex || '';
    const selectedWeight = target.selectedWeight || '';

    let selectedIndexData = dataToUse.filter((x: any) => {
      if (level == 1) return x.Level1ColChild == selectedIndex;
      else if (level == 2) return x.Level2ColChild == selectedIndex;
      else return x.colChild == selectedIndex;
    });
    if (selectedIndexData.length > 0) {
      selectedIndexData = JSON.parse(JSON.stringify(selectedIndexData));
      selectedIndexData.forEach((item: any) => {
        let indexData = dataToUse.filter((x: any) => {
          if (level == 1) {
            return (
              x.rowChild == item.rowChild &&
              x.Level2RowChild == item.Level2RowChild &&
              x.Level1RowChild == item.Level1RowChild &&
              x.colChild == item.colChild
            );
          } else if (level == 2) {
            return (
              x.rowChild == item.rowChild &&
              x.Level2RowChild == item.Level2RowChild &&
              x.Level1RowChild == item.Level1RowChild &&
              x.colChild == item.colChild &&
              x.Level1ColChild == item.Level1ColChild
            );
          } else
            return (
              x.rowChild == item.rowChild &&
              x.Level2RowChild == item.Level2RowChild &&
              x.Level1RowChild == item.Level1RowChild
            );
        });
        if (indexData.length > 0) {
          indexData.forEach((indexItem: any) => {
            if (selectedWeight == 'Unweighted') {
              indexItem.totalUnweightedShare = isNaN(
                indexItem.totalUnweightedShare / item.totalUnweightedShare
              )
                ? null
                : isFinite(
                  indexItem.totalUnweightedShare / item.totalUnweightedShare
                )
                  ? indexItem.totalUnweightedShare / item.totalUnweightedShare
                  : null;
              indexItem.answerUnweightedShare = isNaN(
                indexItem.answerUnweightedShare / item.answerUnweightedShare
              )
                ? null
                : isFinite(
                  indexItem.answerUnweightedShare / item.answerUnweightedShare
                )
                  ? indexItem.answerUnweightedShare / item.answerUnweightedShare
                  : null;
              indexItem.totalUnweightedShare_Index =
                indexItem.totalUnweightedShare;
              indexItem.answerUnweightedShare_Index =
                indexItem.answerUnweightedShare;
            } else {
              indexItem.totalWeightedShare = isNaN(
                indexItem.totalWeightedShare / item.totalWeightedShare
              )
                ? null
                : isFinite(
                  indexItem.totalWeightedShare / item.totalWeightedShare
                )
                  ? indexItem.totalWeightedShare / item.totalWeightedShare
                  : null;
              indexItem.answerWeightedShare = isNaN(
                indexItem.answerWeightedShare / item.answerWeightedShare
              )
                ? null
                : isFinite(
                  indexItem.answerWeightedShare / item.answerWeightedShare
                )
                  ? indexItem.answerWeightedShare / item.answerWeightedShare
                  : null;
              indexItem.totalWeightedShare_Index = indexItem.totalWeightedShare;
              indexItem.answerWeightedShare_Index =
                indexItem.answerWeightedShare;
            }
          });
        }
      });
    }
  }
}
